import { Button, CssBaseline, Grid, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useDispatch, useSelector } from 'react-redux';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import loader from 'src/components/LoaderImg/loader.gif';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import { Seo } from 'src/components/seo';
import { baseApiUrl, copyCodeDescriptionTreatment } from 'src/config';
import { LoadingButton } from '@mui/lab';
import { getNoteTemplateAction } from 'src/redux/actions/dailySessionNotes';
import {
  pullUuidAction,
  resetForLoaderDocumentation,
  resetUuidAction,
} from 'src/redux/actions/documentationAction';
import BackModalConfirmation from './back-modal-confirmation';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { getAccessToken } from 'src/routes/AuthContext';
import { getTherapistCred } from 'src/utils/get-therapist-credential';
import DynamicForm from '../dynamic-form';
import EmptyState from 'src/pages/details-page/unlockTherapist/Placeholder.svg';
import { getSelectedFacilityName } from 'src/utils/get-selected-facility-name';
import { formatLastSavedDate } from 'src/utils/formatLastSavedDate';
import useFeatureToggle, {useFeatureToggleWhenReady} from 'src/hooks/use-feature-toggle';
import useCurrentUser from 'src/hooks/use-current-user';
import CopyTextBank from '../copy-text-bank';
import { DrawerMain } from '../copy-text-bank/drawer-configuration';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReForm from '../reform';

const loaderCSS: CSSProperties = {
  position: 'fixed',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  WebkitUserSelect: 'none',
  KhtmlUserSelect: 'none',
  MozUserSelect: 'none',
  userSelect: 'none',
  zIndex: 9999,
};

type Props = {
  dailySessionNotesInfo: any;
  selectedPatient: any;
  setOpen: any;
  dailySessionNotesInfoResponse: any;
  setOpenEditNotes: any;
  createSoapNotes: any;
  selectedDate: any;
};

function FinalizeNotes({
  dailySessionNotesInfo,
  selectedPatient,
  setOpen,
  dailySessionNotesInfoResponse,
  setOpenEditNotes,
  createSoapNotes,
  selectedDate,
}: Props) {
  const dispatch = useDispatch();
  const getNoteTemplate = useSelector((state: any) => state.getNoteTemplateReducer);
  const pullUuidResponse = useSelector((state: any) => state.pullUuidRducder);
  const isDocLoader = useSelector((state: any) => state.loaderDocumenattionReducer);
  const soapNotesResponse = useSelector((state: any) => state?.updateSoapNoteReducer);
  const selectedPatientName = `${selectedPatient?.lastName} ${selectedPatient?.firstName}`;
  const [lastAutosaveTimestamp, setLastAutosaveTimestamp] = useState<Date>(new Date());
  const lockOtherPatient = useSelector((state: any) => state.getLockDailySessionNote);
  const formContainerRef = useRef<HTMLDivElement | null>(null);

  const [isCopyCodeDescriptionEnabled] = useFeatureToggle(
    copyCodeDescriptionTreatment,
    'ENTERPRISE'
  );

  const [discipline] = useCurrentUser([
    'discipline',
  ]);

  const getCleanUuid = () => {
    return pullUuidResponse?.response?.trim() || '';
  };

  useEffect(() => {
    if (!dailySessionNotesInfo?.dailySessionNote?.uuid) return;
    dispatch(getNoteTemplateAction(dailySessionNotesInfo?.dailySessionNote?.uuid));
  }, [dispatch, dailySessionNotesInfo, selectedPatient]);

  useEffect(() => {
    if (!dailySessionNotesInfo?.prefillKey?.split('/')[3]) return;
    if (dailySessionNotesInfo?.prefillKey?.split('/')[3]) {
      dispatch(
        pullUuidAction(
          {
            notesId: dailySessionNotesInfo?.dailySessionNote?.uuid,
            formId: dailySessionNotesInfo?.prefillKey?.split('/')[3],
            entityType: 'SOAP',
          },
          {
            forEdit: dailySessionNotesInfo?.dailySessionNote?.status === 'FINALIZED' ? true : false,
            discipline,
          }
        )
      );
      return;
    }
  }, [dailySessionNotesInfo, discipline, dispatch]);

  useEffect(() => {
    if (
      getNoteTemplate?.response?.data?.formId &&
      !dailySessionNotesInfo?.prefillKey?.split('/')[3]
    ) {
      dispatch(
        pullUuidAction({
          notesId: dailySessionNotesInfo?.dailySessionNote?.uuid,
          formId: getNoteTemplate?.response?.data?.formId,
          entityType: 'SOAP',
        })
      );
    }

    if (
      getNoteTemplate?.response?.data?.reformFormId &&
      !dailySessionNotesInfo?.prefillKey?.split('/')[3]
    ) {
      const formId: string = getNoteTemplate?.response?.data?.link?.split('/')[3] || '';
      dispatch(
        pullUuidAction({
          notesId: dailySessionNotesInfo?.dailySessionNote?.uuid,
          formId,
          entityType: 'SOAP',
        })
      );
    }
  }, [getNoteTemplate?.response?.data]);

  const pullFormID = () => {
    if (
      getNoteTemplate?.response?.data?.formId &&
      !dailySessionNotesInfo?.prefillKey?.split('/')[3]
    ) {
      return getNoteTemplate?.response?.data?.formId;
    } else {
      return dailySessionNotesInfo?.prefillKey?.split('/')[3];
    }
  };

  useEffect(() => {
    if (!isDocLoader.loader && isDocLoader.response === null) return;
    if (!isDocLoader.loader) {
      dispatch(resetUuidAction());
      dispatch(resetForLoaderDocumentation());
      setOpenEditNotes(false);
    }
  }, [isDocLoader]);

  const [openBackModal, setBackModal] = useState(false);
  const handleBack = () => {
    setBackModal(true);
  };

  const handleCloseBack = () => {
    setBackModal(false);
  };

  const [openTextBank, setOpenTextBank] = React.useState(false);

  const handleTextBankOpen = () => {
    setOpenTextBank(true);
  };

  const handleTextBankClose = () => {
    setOpenTextBank(false);
  };

  const formElement = () => {
    if(getNoteTemplate?.loading) {
      return (
        <img
          src={loader}
          alt="Loading..."
        />
      );
    }

    if(getNoteTemplate?.response?.data?.reformFormId) {
      return (
        <Box
          style={{
            width: openTextBank ? '125%' : 'auto',
          }}
          ref={formContainerRef}
        >
          <ReForm
            uuid={getCleanUuid()}
            formId={getNoteTemplate.response.data.reformFormId}
            disablePadding={true}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{ height: '100%' }}
        py={3}
      >
        <Box
          style={{
            maxHeight: '70vh',
            overflow: 'auto',
            width: openTextBank ? '125%' : 'auto',
          }}
          ref={formContainerRef}
        >
          <DynamicForm
            uuid={getCleanUuid()}
            formId={pullFormID()}
            autosaveTimestamp={lastAutosaveTimestamp}
            setAutosaveTimestamp={setLastAutosaveTimestamp}
            containerRef={formContainerRef}
          />
        </Box>
      </Box>
    );
  }

  const showDynamicFormsSaveDate = () => {
    if(!pullUuidResponse) {
      return false;
    }

    if(getNoteTemplate?.response?.data?.reformFormId) {
      return false;
    }

    return true;
  }

  return dailySessionNotesInfoResponse?.loading ? (
    <img
      src={loader}
      alt="Loading..."
      style={{ margin: 'auto', display: 'block', marginTop: '10%' }}
    />
  ) : !dailySessionNotesInfo && !dailySessionNotesInfoResponse.loading ? (
    <Typography
      sx={{ fontSize: '18px !important' }}
      variant="h6"
    >
      {!selectedPatient
        ? 'No Records Found'
        : `Billing codes are not available for this patient - "${selectedPatientName}"`}
    </Typography>
  ) : (
    <>
      <Seo title="Patient: Session Notes" />

      {isDocLoader.loader && (
        <Grid sx={loaderCSS}>
          <img
            src={loader}
            alt="Loading..."
          />
        </Grid>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: selectedPatient?.name || selectedPatient?.notesId ? 1 : 0,
        }}
      >
        <Stack
          justifyContent="space-between"
          flexDirection="row"
          alignItems="center"
        >
          <Typography
            sx={{ fontSize: '18px !important' }}
            variant="h6"
          >
            {selectedPatient && selectedPatientName
              ? selectedPatientName?.endsWith("'s")
                ? selectedPatientName
                : `${selectedPatientName}'s` + ' notes'
              : 'No Records Found'}
            {isCopyCodeDescriptionEnabled && (
              <Button
                startIcon={<ContentCopyIcon />}
                onClick={handleTextBankOpen}
              >
                Text Copy
              </Button>
            )}
          </Typography>

          {!selectedPatient?.notesId && !selectedPatient?.name && (
            <Stack
              flexDirection="row"
              gap="1rem"
            >
              <LoadingButton
                color="primary"
                loading={soapNotesResponse?.loading}
                variant="contained"
                onClick={createSoapNotes}
                disabled={lockOtherPatient?.response?.data}
              >
                Start Note
              </LoadingButton>
            </Stack>
          )}
          {selectedPatient && (selectedPatient?.name || selectedPatient?.notesId) && (
            <Stack
              flexDirection="row"
              gap="1rem"
              alignItems="center"
            >
              {dailySessionNotesInfo?.dailySessionNote?.status !== 'NOT_STARTED' &&
                soapNotesResponse?.response?.code !== 'BAD_REQUEST' && (
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                )}

              {showDynamicFormsSaveDate() && (
                <>
                  <Typography
                    variant="body1"
                    component="div"
                  >
                    Last saved: {formatLastSavedDate(lastAutosaveTimestamp)}
                  </Typography>
                </>
              )}

              {soapNotesResponse?.response?.code !== 'BAD_REQUEST' && (
                <Button
                  color="primary"
                  variant="outlined"
                  startIcon={<ArticleOutlinedIcon />}
                  onClick={() => setOpen((prev: any) => !prev)}
                  disabled={dailySessionNotesInfoResponse?.loading}
                >
                  Therapy Info
                </Button>
              )}
            </Stack>
          )}
        </Stack>
        <Box sx={{ display: 'flex', height: '100%' }}>
          <CssBaseline />
          <DrawerMain open={openTextBank}>
            {(getNoteTemplate.loading &&
              dailySessionNotesInfo?.dailySessionNote?.status === 'NOT_STARTED') ||
            dailySessionNotesInfoResponse?.loading ? (
              <img
                src={loader}
                alt="Loading..."
                style={{
                  width: '150px',
                  margin: 'auto',
                  alignContent: 'center',
                  marginTop: '10%',
                  display: 'block',
                }}
              />
            ) : selectedPatient?.locked ? (
              <>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                  py={3}
                >
                  <img
                    src={EmptyState}
                    alt=""
                  />
                  <Typography
                    variant="h5"
                    color="initial"
                    sx={{ color: '#6C737F' }}
                  >
                    The editing period for this Daily Session Note has ended. Contact your DOR for
                    editing permissions.{' '}
                  </Typography>
                </Box>
              </>
            ) : (
              (selectedPatient?.name || selectedPatient?.notesId) && pullUuidResponse?.response && formContainerRef !== null && (
                <>{formElement()}</>
              )
            )}
          </DrawerMain>

          <Box sx={{ marginTop: '50px' }}>
            <CopyTextBank
              open={openTextBank}
              handleDrawerClose={handleTextBankClose}
              source="under_dsn"
            />
          </Box>
        </Box>
      </Box>
      {!selectedPatient?.name && soapNotesResponse?.response?.code === 'BAD_REQUEST' && (
        <Stack sx={{ marginTop: '2rem' }}>
          <Typography variant="h5">{soapNotesResponse?.response?.message}</Typography>
        </Stack>
      )}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={handleCloseBack}
        aria-labelledby="customized-dialog-title"
        open={openBackModal}
      >
        <BackModalConfirmation
          close={handleCloseBack}
          confirm={() => setOpenEditNotes(false)}
        />
      </BootstrapDialog>
    </>
  );
}

export default FinalizeNotes;